import type { Plan } from '@/features/subscriptions/types/plans/plan.types'

export interface PlanGeneralDetail {
  label: string
  value: string
  type?: 'text' | 'link' | 'badge'
}

export interface PricingPhase extends Record<string, unknown> {
  id: string
  name: string
  period: string
  items: PricingItem[]
}

export interface PricingItem extends Record<string, unknown> {
  id: string
  name: string
  type: string
  repeats: string
  billed: string
  discount?: string
  amount: string
}

export function generatePlanGeneralData(planData?: Plan): PlanGeneralDetail[] {
  if (planData) {
    // Transform real plan data into general details format
    return [
      { label: 'Name', value: planData.name },
      { label: 'Description', value: planData.description },
      { label: 'Country', value: 'Bulgaria (BGN)' },
      { label: 'Contract', value: 'Yes - 1 year' },
      { label: 'Trial', value: 'Yes - 14 days, payment method required' },
      { label: 'Billing mode', value: 'Recurring (Auto-charge)' },
      { label: 'Billing Schedule Lable', value: '-' },
      { label: 'Invoice memo', value: 'Standard markup for EUR → USD conversions applied to EU merchant' },
      { label: 'Invoicing Method', value: 'Standard markup for EUR → USD conversions applied to EU merchant' },
      { label: 'Invoice Template', value: 'Standard markup for EUR → USD conversions applied to EU merchant' },
      { label: 'Upgrade', value: 'Allowed' },
      { label: 'Downgrade', value: 'Not allowed' },
    ]
  }

  // Fallback to mock data
  return [
    { label: 'Name', value: 'Monthly Fruit Delivery' },
    { label: 'Description', value: 'CurrencyCloud' },
    { label: 'Country', value: 'Bulgaria (BGN)' },
    { label: 'Contract', value: 'Yes - 1 year' },
    { label: 'Trial', value: 'Yes - 14 days, payment method required' },
    { label: 'Billing mode', value: 'Recurring (Auto-charge)' },
    { label: 'Billing Schedule Lable', value: '-' },
    { label: 'Invoice memo', value: 'Standard markup for EUR → USD conversions applied to EU merchant' },
    { label: 'Invoicing Method', value: 'Standard markup for EUR → USD conversions applied to EU merchant' },
    { label: 'Invoice Template', value: 'Standard markup for EUR → USD conversions applied to EU merchant' },
    { label: 'Upgrade', value: 'Allowed' },
    { label: 'Downgrade', value: 'Not allowed' },
  ]
}

export function generatePlanPricingData(planData?: Plan): PricingPhase[] {
  // For now, return mock data as pricing structure would need to be part of the entity
  // In a real implementation, planData would include pricing phases
  return [
    {
      id: 'phase-1',
      name: planData ? `${planData.name} - Phase 1` : 'Phase name goes here',
      period: '1 Oct - 31 Oct, 2025',
      items: [
        {
          id: 'item-1',
          name: '(Sample) Coconut Bar Soap',
          type: 'Retail',
          repeats: 'Monthly',
          billed: 'In arrears',
          amount: '$120.00'
        },
        {
          id: 'item-2',
          name: '(Sample) Coconut Bar Soap',
          type: 'Retail',
          repeats: 'One time',
          billed: 'In arrears',
          amount: '$120.00'
        },
        {
          id: 'item-3',
          name: '(Sample) Coconut Bar Soap',
          type: 'Retail',
          repeats: 'Monthly',
          billed: 'In arrears',
          amount: '$120.00'
        }
      ]
    },
    {
      id: 'phase-2',
      name: planData ? `${planData.name} - Phase 2` : 'Phase name goes here',
      period: '1 Oct - 31 Oct, 2025',
      items: [
        {
          id: 'item-4',
          name: '(Sample) Coconut Bar Soap',
          type: 'Retail',
          repeats: 'Monthly',
          billed: 'In arrears',
          amount: '$120.00'
        }
      ]
    }
  ]
}

export interface PlanTimelineEvent {
  id: string
  type: string
  description: string
  timestamp: string
  linkText?: string
  linkHref?: string
}

export function generatePlanTimelineEvents(planData?: Plan): PlanTimelineEvent[] {
  if (planData) {
    // Generate timeline events based on real plan data
    return [
      {
        id: '1',
        type: 'schedule',
        description: `Subscription plan "${planData.name}" created`,
        timestamp: planData.created
      },
      {
        id: '2',
        type: 'invoice',
        description: 'Invoice #INV93u4239u4 sent to',
        linkText: 'Customer name',
        linkHref: '/customers/123',
        timestamp: '9 Oct 2025, 14:56'
      },
      {
        id: '3',
        type: 'contract',
        description: 'Customer name signed off the contract',
        timestamp: '9 Oct 2025, 14:56'
      }
    ]
  }

  // Fallback to mock data
  return [
    {
      id: '1',
      type: 'invoice',
      description: 'Invoice #INV93u4239u4 sent to',
      linkText: 'Customer name',
      linkHref: '/customers/123',
      timestamp: '9 Oct 2025, 14:56'
    },
    {
      id: '2',
      type: 'contract',
      description: 'Customer name signed off the contract',
      timestamp: '9 Oct 2025, 14:56'
    },
    {
      id: '3',
      type: 'schedule',
      description: 'Billing schedule created',
      timestamp: '30 Sep, 2025, 14:56'
    }
  ]
}
